export type FormationGuidanceLocale = 'nl' | 'en';
export interface FormationCoachingText {
  introduction?: string;
  suitable_for?: string[];
  strengths?: string[];
  considerations?: string[];
  coaching_tips?: string[];
  in_possession?: string;
  out_of_possession?: string;
}
export interface FormationGuidanceSource {
  id: string;
  title: string;
  publisher?: string | null;
  url: string;
  locator?: string | null;
  accessed_on?: string | null;
  supports?: Partial<Record<FormationGuidanceLocale, string>>;
}
export interface FormationGuidance {
  schema_version: 1;
  status: 'draft';
  translations?: Partial<Record<FormationGuidanceLocale, FormationCoachingText>>;
  position_roles?: Record<string, Partial<Record<FormationGuidanceLocale, string>>>;
  sources?: FormationGuidanceSource[];
}
export const formationGuidanceLimits = Object.freeze({ prose: 4000, listItem: 1000, listItems: 12,
  role: 2000, roles: 64, sources: 20, sourceId: 100, title: 300, publisher: 200,
  url: 2048, locator: 300, support: 1000, bytes: 100 * 1024 });
export interface FormationGuidanceIssue { path: Array<string | number>; code: string }
export interface FormationGuidanceValidationOptions { positionKeys?: Iterable<string> }
export class FormationGuidanceValidationError extends Error {
  readonly code = 'FORMATION_GUIDE_INVALID';
  constructor(readonly errors: FormationGuidanceIssue[]) { super('FORMATION_GUIDE_INVALID'); }
}
export const createFormationCoachingText = (): FormationCoachingText => ({ introduction: '', suitable_for: [],
  strengths: [], considerations: [], coaching_tips: [], in_possession: '', out_of_possession: '' });
export const createFormationGuidance = (): FormationGuidance => ({ schema_version: 1, status: 'draft',
  translations: {}, position_roles: {}, sources: [] });

const record = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const proseFields = ['introduction', 'in_possession', 'out_of_possession'];
const listFields = ['suitable_for', 'strengths', 'considerations', 'coaching_tips'];
const locales = ['nl', 'en'];
const own = (value: object, key: string) => Object.prototype.hasOwnProperty.call(value, key);

/** No text trimming, translation fallback, URL rewriting or missing-field defaults. */
export const getFormationGuidanceIssues = (value: unknown, options: FormationGuidanceValidationOptions = {}): FormationGuidanceIssue[] => {
  if (value === undefined || value === null) return [];
  const issues: FormationGuidanceIssue[] = [];
  const invalid = (path: Array<string | number>, code = 'INVALID_GUIDE') => { issues.push({ path: ['guide', ...path], code }); };
  const extraKeys = (object: Record<string, unknown>, allowed: string[], path: Array<string | number>) => {
    for (const key of Object.keys(object)) if (!allowed.includes(key)) invalid([...path, key]);
  };
  const text = (candidate: unknown, limit: number, path: Array<string | number>, nonempty = false) => {
    if (typeof candidate !== 'string') { invalid(path); return; }
    if ([...candidate].length > limit || nonempty && !candidate.trim().length
      || /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\uD800-\uDFFF]/u.test(candidate)
      || /<[^>]+>/.test(candidate)) invalid(path);
  };
  const languageText = (candidate: unknown, limit: number, path: Array<string | number>) => {
    if (!record(candidate)) { invalid(path); return; }
    extraKeys(candidate, locales, path);
    for (const locale of locales) if (own(candidate, locale)) text(candidate[locale], limit, [...path, locale]);
  };
  if (!record(value)) { invalid([]); return issues; }
  extraKeys(value, ['schema_version', 'status', 'translations', 'position_roles', 'sources'], []);
  if (value.schema_version !== 1) invalid(['schema_version'], 'UNSUPPORTED_GUIDE_VERSION');
  if (value.status !== 'draft') invalid(['status']);
  if (own(value, 'translations')) {
    if (!record(value.translations)) invalid(['translations']);
    else {
      extraKeys(value.translations, locales, ['translations']);
      for (const locale of locales) if (own(value.translations, locale)) {
        const translated = value.translations[locale];
        if (!record(translated)) { invalid(['translations', locale]); continue; }
        extraKeys(translated, [...proseFields, ...listFields], ['translations', locale]);
        for (const field of proseFields) if (own(translated, field)) text(translated[field], formationGuidanceLimits.prose, ['translations', locale, field]);
        for (const field of listFields) if (own(translated, field)) {
          const items = translated[field];
          if (!Array.isArray(items) || items.length > formationGuidanceLimits.listItems) { invalid(['translations', locale, field]); continue; }
          items.forEach((item, index) => text(item, formationGuidanceLimits.listItem, ['translations', locale, field, index]));
        }
      }
    }
  }
  if (own(value, 'position_roles')) {
    if (!record(value.position_roles)) invalid(['position_roles']);
    else {
      const positionKeys = options.positionKeys === undefined ? undefined : new Set(options.positionKeys);
      if (Object.keys(value.position_roles).length > formationGuidanceLimits.roles) invalid(['position_roles']);
      for (const [key, role] of Object.entries(value.position_roles)) {
        languageText(role, formationGuidanceLimits.role, ['position_roles', key]);
        if (positionKeys && !positionKeys.has(key)) invalid(['position_roles', key], 'ORPHAN_POSITION_ROLE');
      }
    }
  }
  if (own(value, 'sources')) {
    if (!Array.isArray(value.sources) || value.sources.length > formationGuidanceLimits.sources) invalid(['sources']);
    else {
      const ids = new Set<string>();
      value.sources.forEach((source, index) => {
        const path = ['sources', index];
        if (!record(source)) { invalid(path); return; }
        extraKeys(source, ['id', 'title', 'publisher', 'url', 'locator', 'accessed_on', 'supports'], path);
        text(source.id, formationGuidanceLimits.sourceId, [...path, 'id'], true);
        if (typeof source.id === 'string') {
          if (ids.has(source.id)) invalid([...path, 'id'], 'DUPLICATE_SOURCE_ID');
          ids.add(source.id);
        }
        text(source.title, formationGuidanceLimits.title, [...path, 'title']);
        text(source.url, formationGuidanceLimits.url, [...path, 'url'], true);
        try {
          if (typeof source.url !== 'string' || !/^https?:\/\//i.test(source.url)) throw new Error();
          const url = new URL(source.url);
          const authority = source.url.match(/^https?:\/\/([^/?#]*)/i)?.[1];
          if (!['http:', 'https:'].includes(url.protocol) || !url.hostname || !authority || /\s/u.test(source.url) || authority.includes('@')) throw new Error();
        } catch { invalid([...path, 'url']); }
        for (const [field, limit] of [['publisher', formationGuidanceLimits.publisher], ['locator', formationGuidanceLimits.locator]] as const) {
          if (own(source, field) && source[field] !== null) text(source[field], limit, [...path, field]);
        }
        if (own(source, 'accessed_on') && source.accessed_on !== null) {
          const date = source.accessed_on;
          if (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date) || date.startsWith('0000')
            || Number.isNaN(Date.parse(`${date}T00:00:00Z`)) || new Date(`${date}T00:00:00Z`).toISOString().slice(0, 10) !== date) invalid([...path, 'accessed_on']);
        }
        if (own(source, 'supports')) languageText(source.supports, formationGuidanceLimits.support, [...path, 'supports']);
      });
    }
  }
  try {
    if (new TextEncoder().encode(JSON.stringify(value)).length > formationGuidanceLimits.bytes) invalid([], 'GUIDE_TOO_LARGE');
  } catch { invalid([]); }
  return issues;
};

/** Validate before editing or writing; return an independent JSON copy without filling hidden locales. */
export const prepareFormationGuidance = (
  value: unknown, options: FormationGuidanceValidationOptions = {},
): FormationGuidance | null | undefined => {
  const issues = getFormationGuidanceIssues(value, options);
  if (issues.length) throw new FormationGuidanceValidationError(issues);
  return value === undefined || value === null ? value : structuredClone(value) as FormationGuidance;
};

/** Order-independent object equality; list order, absent fields and null remain significant. */
export const formationGuidanceEqual = (left: unknown, right: unknown): boolean => {
  if (left === right) return true;
  if (Array.isArray(left) && Array.isArray(right)) return left.length === right.length && left.every((item, index) => formationGuidanceEqual(item, right[index]));
  if (!record(left) || !record(right)) return false;
  const keys = Object.keys(left);
  return keys.length === Object.keys(right).length && keys.every(key => own(right, key) && formationGuidanceEqual(left[key], right[key]));
};
